import {
  useWSModalCustom,
  useWSSnackbar,
  WSActions,
  WSAlert,
  WSForm,
  WSList,
  WSModal,
  WSModalCustomProps,
  WSText
} from "@wingspanhq/fe-component-library";
import { WorkLog } from "@wingspanhq/wingspan-api-sdk";
import { queriesWorkLogs } from "@query";
import { PublicCommentField } from "../../components/PublicCommentField";

type FormData = {
  comment: string;
};

type Props = {
  workLog: WorkLog;
  contractorName?: string;
} & WSModalCustomProps<{
  success?: boolean;
}>;

export const ModalApproveWorkLog: React.FC<Props> = ({
  workLog,
  contractorName = "the contractor",
  ...modalProps
}) => {
  const { openSnackbar } = useWSSnackbar();
  const [approveWorkLog, approveMeta] =
    queriesWorkLogs.approveWorkLog.useMutation();

  const handleApprove = async (formData: FormData) => {
    // TODO(v3): approveWorkLog takes no `comment` field — the public comment
    // entered here (formData.comment) is intentionally NOT sent/persisted on
    // approve; the comment UI is kept for parity but the value is dropped.
    approveWorkLog(
      {
        workLogId: workLog.id!
      },
      {
        onSuccess: () => {
          modalProps.onClose({ success: true });
          openSnackbar({
            icon: { name: "check" },
            message: "Work log approved successfully"
          });
        },
        onError: () => {
          openSnackbar({
            type: "warning",
            message: "Failed to approve work log. Please try again."
          });
        }
      }
    );
  };

  return (
    <WSModal title="Approve work log" {...modalProps}>
      <WSForm<FormData>
        onSubmit={handleApprove}
        defaultValues={{ comment: "" }}
      >
        <WSList gap="XL">
          <WSText.ParagraphSm color="gray500">
            Are you sure you want to approve the work log?
          </WSText.ParagraphSm>

          <WSAlert
            theme="info"
            title="About approved work logs"
            icon="info-circle"
          >
            This work log will be converted to an invoice and added to your next
            payment cycle.
            {"\n\n"}
            Note: You can still correct unpaid invoices and work logs if needed.
            Paid invoices and related work logs cannot be changed.
          </WSAlert>

          <PublicCommentField
            description={`Comment is visible to both you and the contractor. Please provide specific details that will inform ${contractorName} about the decision.`}
            placeholder="Provide specific details about the decision"
          />

          <WSActions
            alignment="fill"
            buttons={[
              {
                kind: "Secondary",
                label: "No, go back",
                type: "button",
                onClick: () => {
                  modalProps.onClose({ success: false });
                }
              },
              {
                kind: "Primary",
                label: "Yes, approve work log",
                loading: approveMeta.isLoading
              }
            ]}
          />
        </WSList>
      </WSForm>
    </WSModal>
  );
};

export const useModalApproveWorkLog = () =>
  useWSModalCustom(ModalApproveWorkLog);
